<template>
  <AppPageContainer :custom="1600" :min-height="0" class="sp-page">
    <UiPageHeader design="new" title="Sales Shift Planner" compact />
    <ShiftPlannerNav />
    <div class="ui-page">
      <AppFilterToolbar>
        <template v-if="model && canMutateSchedule" #create>
          <AppButton design="new" @click.native="openLeaveModal">Vacation / sick leave</AppButton>
        </template>
        <app-select
          v-if="model && canSelectManager"
          v-model="selectedUserId"
          design="new"
          input-id="shift-planner-manager"
          aria-label="Manager"
          placeholder="Manager"
          :options="participants"
          :reduce="(item) => item.user.id"
          :display-field="participantLabel"
          :get-option-label="participantLabel"
          :clearable="false"
          @input="loadMonth"
        />
        <app-select
          v-if="model"
          v-model="timezoneName"
          design="new"
          input-id="shift-planner-timezone"
          aria-label="Display timezone"
          placeholder="Display timezone"
          :options="timezoneOptions"
          :reduce="(item) => item.value"
          :clearable="false"
          @input="saveTimezone"
        />
        <template #actions>
          <AppIconButton v-if="model && canMutateSchedule" label="Manage shifts" tooltip-placement="left" :disabled="scheduleOpening" :aria-busy="scheduleOpening ? 'true' : 'false'" @click="openScheduleModal"><i :class="scheduleOpening ? 'pi pi-spin pi-spinner' : 'pi pi-sliders-h'" aria-hidden="true" /></AppIconButton>
          <AppIconButton v-if="model && canMutateSchedule" label="Plan days off" tooltip-placement="left" @click="openDayOffModal"><i class="pi pi-calendar" aria-hidden="true" /></AppIconButton>
          <AppIconButton v-if="model && (viewingPeerSchedule || returningToMySchedule)" label="Show my schedule" tooltip-placement="left" :disabled="returningToMySchedule" :aria-busy="returningToMySchedule ? 'true' : 'false'" @click="showMySchedule"><i :class="returningToMySchedule ? 'pi pi-spin pi-spinner' : 'pi pi-user'" aria-hidden="true" /></AppIconButton>
          <AppIconButton label="Info & rules" tooltip-placement="left" @click="openRulesModal"><i class="pi pi-info-circle" aria-hidden="true" /></AppIconButton>
          <AppIconButton label="Legacy calendar" tooltip-placement="left" @click="$router.push({ name: 'sales-work-schedule' })"><i class="pi pi-history" aria-hidden="true" /></AppIconButton>
        </template>
      </AppFilterToolbar>
      <AppErrorState v-if="error" :message="error" @retry="loadMonth" />
      <AppContentSkeleton v-if="loading && !model" layout="block" label="Loading schedule" />

      <template v-else-if="model">
      <section
        class="sp-grid sp-grid--manager-metrics"
        aria-label="Monthly progress"
      >
        <UiMetricCard
          design="new"
          padding="compact"
          label="Hours"
          :value="`${summary.hours} / ${summary.hours_max} h`"
          :help="monthlyHoursHelp"
          :status="metricViolation('hours_min', 'hours_max')"
          :tone="
            summary.hours >= summary.hours_min &&
            summary.hours <= summary.hours_max
              ? 'green'
              : 'red'
          "
        />
        <UiMetricCard
          design="new"
          padding="compact"
          :label="weekendMetricLabel"
          :value="`${summary.weekend_work} / ${summary.weekend_work_required}`"
          :help="weekendMetricHelp"
          :status="metricViolation('weekend_min')"
          :tone="
            summary.weekend_work >= summary.weekend_work_required
              ? 'green'
              : 'red'
          "
        />
        <UiMetricCard
          design="new"
          padding="compact"
          label="Day off"
          :value="`${summary.planned_days_off} / ${summary.planned_days_off_required}`"
          :help="`Choose exactly ${summary.planned_days_off_required} planned days off for this month. A day off cannot contain shifts.`"
          :status="metricViolation('day_off_count')"
          :tone="
            summary.planned_days_off === summary.planned_days_off_required
              ? 'green'
              : 'red'
          "
        />
        <UiMetricCard
          design="new"
          padding="compact"
          label="Absence"
          :value="summary.approved_absence_days"
          :meta="summary.requested_absence_days
            ? `${summary.requested_absence_days} pending day${summary.requested_absence_days === 1 ? '' : 's'}`
            : ''"
          help="Requested or approved vacation and sick leave appear here. Approved leave blocks shifts."
          tone="blue"
        />
      </section>

      <AppCard design="new" class="sp-calendar-card" :aria-busy="loading ? 'true' : 'false'">
        <div class="sp-calendar-guide">
          <LegendDropdown :items="legendItems" />
          <strong class="sp-calendar-month">{{ selectedMonthLabel }}</strong>
          <p v-if="canMutateSchedule">
            Use Manage shifts above the calendar to edit the full month.
            Managers can edit schedules starting three weeks ahead.
          </p>
          <p v-else>
            You are viewing a colleague’s schedule in read-only mode.
          </p>
        </div>
        <div class="sp-calendar-navigation">
          <button
            class="sp-calendar-nav-button"
            type="button"
            aria-label="Previous month"
            :disabled="Boolean(monthNavigation)"
            :aria-busy="monthNavigation === -1 ? 'true' : 'false'"
            @click="changeMonth(-1)"
          >
            <i v-if="monthNavigation === -1" class="pi pi-spin pi-spinner" aria-hidden="true" />
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <path d="m15 5-7 7 7 7" />
            </svg>
          </button>
          <div class="sp-calendar-scroll">
            <div class="sp-weekdays" aria-hidden="true">
              <span v-for="label in weekdays" :key="label">{{ label }}</span>
            </div>
            <div class="sp-calendar">
              <button
                v-for="day in model.days"
                :key="day.date"
                v-tooltip="
                  dayLockedReason(day)
                    ? {
                        content: dayLockedReason(day),
                        placement: 'top',
                        offset: 8,
                        classes: 'tooltip--new',
                      }
                    : false
                "
                class="sp-day"
                :class="dayClasses(day)"
                type="button"
                :aria-label="dayAriaLabel(day)"
                :aria-pressed="selectedDate === day.date"
                @click="openDayModal(day)"
              >
                <span class="sp-day__shift-stack">
                  <span
                    v-for="(template, index) in model.templates"
                    :key="template.code"
                    class="sp-day__shift"
                    :class="{
                      'is-active': day.assignment_mask[index] === '1',
                    }"
                    :title="template.code"
                  >
                    <i :class="`sp-segment--${template.kind}`" />
                  </span>
                </span>

                <span class="sp-day__date">
                  <strong>{{ formatDayNumber(day.date) }}</strong>
                  <small>{{ formatDayMonth(day.date) }}</small>
                  <i v-if="todayDate === day.date" aria-label="Today" />
                  <em v-if="day.is_day_off">Day off</em>
                  <em
                    v-else-if="absenceFor(day.date)"
                    :title="absenceTitle(day.date)"
                  >{{ absenceLabel(day.date) }}</em>
                </span>

                <span
                  v-if="!effectiveDayEditable(day)"
                  class="sp-day__lock"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
          <button
            class="sp-calendar-nav-button"
            type="button"
            aria-label="Next month"
            :disabled="Boolean(monthNavigation)"
            :aria-busy="monthNavigation === 1 ? 'true' : 'false'"
            @click="changeMonth(1)"
          >
            <i v-if="monthNavigation === 1" class="pi pi-spin pi-spinner" aria-hidden="true" />
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9 5 7 7-7 7" />
            </svg>
          </button>
        </div>
      </AppCard>
    </template>

    </div>

    <modal
      name="planner-day"
      :width="700"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="false"
      class="sp-planner-modal"
      @opened="focusModalHeader('dayHeader')"
    ><div class="sp-modal" role="dialog" aria-modal="true" aria-labelledby="planner-day-title">
      <ModalHeader
        ref="dayHeader"
        design="new"
        title-id="planner-day-title"
        eyebrow="Daily schedule"
        :title="selectedDate ? formatSelectedDate(selectedDate) : 'Daily schedule'"
        @close="closeDayModal"
      />
      <div class="sp-modal__body">
        <div class="sp-timezone-preview">
          <div>
            <span>Planning timezone</span>
            <strong>{{ model ? model.plan.planning_timezone : 'UTC' }}</strong>
          </div>
          <div>
            <span>Display timezone</span>
            <strong>{{ selectedTimezoneLabel }}</strong>
          </div>
        </div>
        <div v-if="dayError" class="sp-error" role="alert">{{ dayError }}</div>
        <label
          v-if="selectedDayRequiresReason"
          class="sp-modal__field sp-modal__field--separated sp-supervisor-reason"
          :class="{ 'sp-supervisor-reason--required': !hasOverrideReason }"
          for="planner-day-reason"
        >
          <span class="sp-field-label">
            Supervisor change reason<span class="sp-required-marker" aria-hidden="true">*</span>
          </span>
          <small id="planner-day-reason-help">
            Required before current-week schedule changes can be saved.
          </small>
          <app-input
            id="planner-day-reason"
            v-model="overrideReason"
            design="new"
            class="sp-supervisor-reason__input"
            :error="!hasOverrideReason"
            :aria-invalid="!hasOverrideReason"
            aria-describedby="planner-day-reason-help"
            aria-required="true"
            placeholder="Describe why this schedule is being changed"
          />
        </label>
        <div v-if="dayLoading" class="sp-skeleton" />
        <div v-else class="sp-day-shifts">
          <article
            v-for="shift in dayShifts"
            :key="shift.id"
            class="sp-day-shift"
            :class="{ 'is-selected': ownAssignment(shift) }"
          >
            <div class="sp-day-shift__copy">
              <div class="sp-day-shift__title">
                <strong>{{ shift.template.code }}</strong>
                <UiStatusBadge
                  design="new"
                  compact
                  :tone="shift.template.kind === 'night' ? 'blue' : 'neutral'"
                  :label="shift.template.kind === 'night' ? 'Night' : 'Day'"
                />
                <span class="sp-day-shift__company-time">
                  Company: {{ shift.template.start_local_time }}–{{ shift.template.end_local_time }}
                </span>
              </div>
              <span class="sp-day-shift__local">{{ timezoneName }}: {{ localShiftRange(shift) }}</span>
              <small>{{ rosterLabel(shift) }}</small>
            </div>
            <div class="sp-day-shift__actions">
              <UiStatusBadge
                design="new"
                compact
                :tone="capacityTone(shift)"
                :label="capacityLabel(shift)"
              />
              <AppButton
                v-if="canToggleDayShift(shift)"
                design="new"
                :theme="ownAssignment(shift) ? 'error' : 'default'"
                size="small"
                @click.native="toggleDayShift(shift)"
              >{{ ownAssignment(shift) ? 'Remove' : 'Add shift' }}</AppButton>
            </div>
          </article>
          <div v-if="!dayLoading && !dayShifts.length" class="sp-empty sp-empty--compact">
            <strong>No shifts available</strong>
            <p>No shift instances are available for this date.</p>
          </div>
        </div>
      </div>
      <footer class="sp-modal__footer">
        <span class="sp-schedule-save-state">{{ daySaving ? 'Saving change…' : 'Changes save automatically' }}</span>
        <AppButton design="new" :disabled="daySaving" @click.native="closeDayModal">Done</AppButton>
      </footer>
    </div></modal>

    <modal
      name="planner-rules"
      :width="680"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      class="sp-planner-modal"
      @opened="focusModalHeader('rulesHeader')"
      ><div class="sp-modal" role="dialog" aria-modal="true" aria-labelledby="planner-rules-title">
        <ModalHeader
          ref="rulesHeader"
          design="new"
          title-id="planner-rules-title"
          eyebrow="Planning guide"
          title="How weekly planning works"
          @close="closeRulesModal"
        />
        <div class="sp-modal__body">
          <p>
            Month is the display, while editing is controlled automatically by
            company-time weeks.
          </p>
          <div class="sp-rule-list">
            <div>
              <strong>Three weeks ahead</strong
              ><span>Weeks starting at least three weeks from now are open for editing.</span>
            </div>
            <div>
              <strong>Current and next two weeks</strong
              ><span>Locked for managers; supervisors can update shifts that have not started.</span>
            </div>
            <div>
              <strong>Daily limit</strong
              ><span>Maximum three shifts per calendar day.</span>
            </div>
            <div>
              <strong>Consecutive limit</strong
              ><span
                >Maximum three adjacent shifts, including across dates.</span
              >
            </div>
            <div>
              <strong>Weekly rest</strong
              ><span>Every Monday–Sunday week must contain at least two full days without shifts.</span>
            </div>
            <div>
              <strong>Company time</strong
              ><span
                >{{ model ? model.plan.planning_timezone : "" }} defines week
                boundaries.</span
              >
            </div>
          </div>
        </div>
    </div></modal
    >

    <modal
      name="planner-schedule"
      :width="1120"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="false"
      class="sp-planner-modal"
      @opened="focusModalHeader('scheduleHeader')"
      ><div class="sp-modal sp-schedule-modal" role="dialog" aria-modal="true" aria-labelledby="planner-schedule-title">
        <ModalHeader
          ref="scheduleHeader"
          design="new"
          title-id="planner-schedule-title"
          eyebrow="Monthly shift plan"
          title="Manage daily schedule"
          @close="closeScheduleModal"
        />
        <div class="sp-modal__body">
          <div class="sp-schedule-summary">
            <div>
              <span>Scheduled hours</span>
              <strong>{{ summary.hours }} / {{ summary.hours_max }} h</strong>
              <small>Minimum {{ summary.hours_min }} hours</small>
            </div>
            <div>
              <span>{{ weekendMetricLabel }}</span>
              <strong>{{ summary.weekend_work }} / {{ summary.weekend_work_required }}</strong>
              <small>Minimum {{ summary.weekend_work_required }} {{ weekendMetricUnit }}</small>
            </div>
            <p>
              Select up to three shifts per day and keep at least two full
              days off in every week. Changes are saved automatically.
            </p>
          </div>

          <label
            v-if="scheduleReasonRequired"
            class="sp-schedule-reason"
            for="planner-schedule-reason"
          >
            <span class="sp-field-label">
              Supervisor change reason<span class="sp-required-marker" aria-hidden="true">*</span>
            </span>
            <app-input
              v-model="overrideReason"
              id="planner-schedule-reason"
              design="new"
              aria-required="true"
              placeholder="Required when changing a locked planning period"
            />
          </label>

          <div class="sp-schedule-board">
            <table class="sp-schedule-table">
              <thead>
                <tr>
                  <th class="sp-schedule-table__day-heading" scope="col">
                    Day
                  </th>
                  <th
                    v-for="template in model ? model.templates : []"
                    :key="template.code"
                    v-tooltip="{
                      content: scheduleTemplateTooltip(template),
                      placement: 'top',
                      offset: 8,
                      classes: 'tooltip--new',
                    }"
                    scope="col"
                  >
                    <strong>{{ template.code }}</strong>
                    <span>{{ compactShiftTime(template) }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="day in currentMonthDays"
                  :key="day.date"
                  :class="scheduleDayClasses(day)"
                >
                  <th scope="row">
                    <div class="sp-schedule-table__date">
                      <strong>{{ formatScheduleDate(day.date) }}</strong>
                      <span v-if="day.is_day_off">Day off</span>
                      <span
                        v-else-if="absenceFor(day.date)"
                        :title="absenceTitle(day.date)"
                      >{{ absenceLabel(day.date) }}</span>
                      <span v-else-if="!effectiveDayEditable(day)">Locked</span>
                    </div>
                  </th>
                  <td
                    v-for="template in model ? model.templates : []"
                    :key="template.code"
                  >
                    <button
                      v-tooltip="{
                        content: scheduleCellTooltip(day, template),
                        placement: 'top',
                        offset: 8,
                        classes: 'tooltip--new',
                      }"
                      class="sp-schedule-cell"
                      :class="scheduleCellClasses(day, template)"
                      type="button"
                      :disabled="!canToggleScheduleShift(day, template)"
                      :aria-label="scheduleCellAriaLabel(day, template)"
                      @click="toggleScheduleShift(day, template)"
                    >
                      <span class="sp-schedule-cell__capacity">
                        <strong>
                          {{ scheduleCapacity(day, template).assigned }}/{{
                            scheduleCapacity(day, template).max
                          }}
                        </strong>
                        <small>
                          {{ scheduleCapacity(day, template).free }} free
                        </small>
                      </span>
                      <span
                        v-if="scheduleShiftSelected(day, template)"
                        class="sp-schedule-cell__selected"
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="m5 12 4 4L19 6" />
                        </svg>
                      </span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <footer class="sp-modal__footer">
          <span class="sp-schedule-save-state">
            {{ scheduleSaving ? "Saving changes…" : "All changes saved" }}
          </span>
          <AppButton
            design="new"
            :disabled="scheduleSaving"
            @click.native="closeScheduleModal"
          >
            Done
          </AppButton>
        </footer>
      </div></modal
    >

    <modal
      name="planner-days-off"
      :width="720"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="false"
      class="sp-planner-modal"
      @opened="focusModalHeader('dayOffHeader')"
      ><div class="sp-modal" role="dialog" aria-modal="true" aria-labelledby="planner-days-off-title">
        <ModalHeader
          ref="dayOffHeader"
          design="new"
          title-id="planner-days-off-title"
          eyebrow="Monthly day off plan"
          :title="`Choose exactly ${summary.planned_days_off_required} days off`"
          @close="closeDayOffModal"
        />
        <div class="sp-modal__body">
          <div
            class="sp-day-off-notice"
            :class="{ 'sp-day-off-notice--error': dayOffError }"
            :role="dayOffError ? 'alert' : 'status'"
            aria-live="polite"
          >
            <span class="sp-day-off-notice__icon" aria-hidden="true">i</span>
            <span>{{
              dayOffError ||
              "Managers can change dates starting three weeks ahead. A day off removes planned shifts on that date."
            }}</span>
          </div>
          <div class="sp-mini-weekdays">
            <span v-for="label in weekdays" :key="label">{{ label }}</span>
          </div>
          <div class="sp-mini-calendar">
            <button
              v-for="day in model ? model.days : []"
              :key="day.date"
              v-tooltip="
                dayOffTooltip(day)
                  ? {
                      content: dayOffTooltip(day),
                      placement: 'top',
                      offset: 8,
                      classes: 'tooltip--new',
                    }
                  : false
              "
              class="sp-mini-day"
              :class="{
                'is-selected': dayOffDraft.includes(day.date),
                'is-weekend': day.is_weekend,
                'is-outside': !day.is_current_month,
                'is-locked': !canToggleDayOff(day),
              }"
              type="button"
              :disabled="!canToggleDayOff(day)"
              @click="toggleDayOff(day)"
            >
              <span>{{ formatDayNumber(day.date) }}</span
              ><small v-if="dayOffDraft.includes(day.date)">Day off</small>
            </button>
          </div>
          <label
            v-if="dayOffRequiresReason"
            class="sp-modal__field sp-modal__field--separated sp-modal__field--day-off-reason"
            for="planner-day-off-reason"
            ><span class="sp-field-label"
              >Supervisor change reason<span class="sp-required-marker" aria-hidden="true">*</span></span
            ><app-input
              id="planner-day-off-reason"
              v-model="overrideReason"
              design="new"
              aria-required="true"
          /></label>
        </div>
        <footer class="sp-modal__footer">
          <UiStatusBadge
            design="new"
            :tone="dayOffDraft.length === summary.planned_days_off_required ? 'green' : 'amber'"
            :label="`${dayOffDraft.length}/${summary.planned_days_off_required} selected`"
          />
          <div class="sp-actions">
            <AppButton design="new" theme="dark" @click.native="closeDayOffModal">
              Cancel</AppButton
            ><AppButton
              design="new"
              :disabled="!canSaveDayOffs"
              @click.native="saveDayOffs"
            >
              Save days off
            </AppButton>
          </div>
        </footer>
      </div></modal
    >

    <modal
      name="planner-leave"
      :width="560"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="false"
      class="sp-planner-modal"
      @opened="focusModalHeader('leaveHeader')"
      ><div class="sp-modal" role="dialog" aria-modal="true" aria-labelledby="planner-leave-title">
        <ModalHeader
          ref="leaveHeader"
          design="new"
          title-id="planner-leave-title"
          eyebrow="Absence request"
          title="Vacation or sick leave"
          @close="closeLeaveModal"
        />
        <div class="sp-modal__body sp-modal-form">
          <div v-if="leaveError" class="sp-error">{{ leaveError }}</div>
          <div class="sp-timezone-preview sp-timezone-preview--single">
            <div>
              <span>Employee</span>
              <strong>{{ selectedManagerLabel }}</strong>
            </div>
          </div>
          <div class="sp-modal__field">
            <span id="planner-leave-type" class="sp-field-label"
              >Leave type<span class="sp-required-marker" aria-hidden="true">*</span></span
            >
            <div
              class="sp-leave-type-switch"
              role="radiogroup"
              aria-labelledby="planner-leave-type"
            >
              <button
                v-for="option in plannerLeaveTypes"
                :key="option.code"
                class="sp-leave-type-switch__option"
                :class="{ 'sp-leave-type-switch__option--active': absence.type === option.code }"
                type="button"
                role="radio"
                :aria-checked="absence.type === option.code ? 'true' : 'false'"
                @click="absence.type = option.code"
              >
                {{ option.name }}
              </button>
            </div>
          </div>
          <div class="sp-modal-form__dates">
            <label class="sp-modal__field" for="planner-leave-from"
              ><span class="sp-field-label"
                >From<span class="sp-required-marker" aria-hidden="true">*</span></span
              ><app-date-picker
                v-model="absence.from_date"
                design="new"
                :input-attr="{ id: 'planner-leave-from', 'aria-required': 'true' }"
                type="date"
                value-type="YYYY-MM-DD"
                format="DD MMM YYYY"
                :disabled-date="isLeaveFromDateDisabled"
                :clearable="false" /></label
            ><label class="sp-modal__field" for="planner-leave-to"
              ><span class="sp-field-label"
                >To<span class="sp-required-marker" aria-hidden="true">*</span></span
              ><app-date-picker
                v-model="absence.to_date"
                design="new"
                :input-attr="{ id: 'planner-leave-to', 'aria-required': 'true' }"
                type="date"
                value-type="YYYY-MM-DD"
                format="DD MMM YYYY"
                :disabled-date="isLeaveToDateDisabled"
                :clearable="false"
            /></label>
          </div>
          <label class="sp-modal__field" for="planner-leave-reason"
            ><span class="sp-field-label"
              >Reason<span
                v-if="leaveReasonRequired"
                class="sp-required-marker"
                aria-hidden="true"
              >*</span></span
            ><app-textarea id="planner-leave-reason" v-model="absence.reason" design="new" />
          </label>
          <p v-if="leaveDays" class="sp-modal__hint">{{ leaveDays }} calendar days (both dates included)</p>
          <SalesLeaveAnnual :annual="leaveAnnual" />
          <div v-if="leaveLocalError" class="sp-error">{{ leaveLocalError }}</div>
          <div v-if="changeableLeaves.length" class="sp-modal__field">
            <span class="sp-field-label">Approved leave in this month</span>
            <ul class="sp-leave-list">
              <li v-for="item in changeableLeaves" :key="item.id">
                <span>{{ item.type.name }}: {{ item.from_date }} – {{ item.to_date }} ({{ item.days }} days)</span>
                <em v-if="item.change_pending">Change pending</em>
                <AppButton
                  v-else
                  design="new"
                  theme="dark"
                  size="small"
                  @click.native="openLeaveChange(item)"
                >
                  Request date change
                </AppButton>
              </li>
            </ul>
          </div>
        </div>
        <footer class="sp-modal__footer">
          <span />
          <div class="sp-actions">
            <AppButton design="new" theme="dark" @click.native="closeLeaveModal">
              Cancel</AppButton
            ><AppButton
              design="new"
              :disabled="saving || !leaveFormValid"
              @click.native="requestLeave"
            >
              Submit request
            </AppButton>
          </div>
        </footer>
      </div></modal
    >

    <SalesLeaveChangeModal
      ref="leaveChangeModal"
      name="planner-leave-change"
      :submit-request="submitLeaveChange"
      :preview-request="previewLeaveChange"
      @created="onLeaveChangeCreated"
    />
  </AppPageContainer>
</template>

<script>
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  format,
  parseISO,
} from 'date-fns'
import AppCard from '@/components/AppCard.vue'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import LegendDropdown from './LegendDropdown.vue'
import ShiftPlannerNav from './PlannerNav.vue'
import UiMetricCard from '@/components/AppMetricCard.vue'
import UiPageHeader from '@/components/AppPageHeader.vue'
import UiStatusBadge from '@/components/AppStatusBadge.vue'
import ModalHeader from '@/components/AppModalHeader.vue'
import debounce from 'lodash/debounce'
import SalesLeaveAnnual from '@/views/employees/sales-leave/SalesLeaveAnnual.vue'
import SalesLeaveChangeModal from '@/views/employees/sales-leave/SalesLeaveChangeModal.vue'
import {
  PAID_CODE,
  businessToday,
  inclusiveDays,
  newRequestStartRange,
  validateNewRequest,
} from '@/views/employees/sales-leave/salesLeave'
import {
  getShiftPlannerDayDetail,
  getShiftPlannerManagerMonth,
  patchShiftPlannerSettings,
  postShiftPlannerAssignment,
  postShiftPlannerAssignmentCancel,
  postEmployeesSalesLeaveRequestPreview,
  postEmployeesSalesLeaveSelfPreview,
  postShiftPlannerLeaveChangeRequest,
  postShiftPlannerLeaveRequest,
  putShiftPlannerDayOffs,
} from '@/http'

const SALES_LEAVE_TYPE_ORDER = ['paid_leave', 'unpaid_leave', 'sick_leave', 'maternity_leave']

export default {
  name: 'SalesShiftPlanner',
  components: {
    AppCard,
    AppContentSkeleton,
    AppErrorState,
    LegendDropdown,
    ShiftPlannerNav,
    UiMetricCard,
    UiPageHeader,
    UiStatusBadge,
    ModalHeader,
    SalesLeaveAnnual,
    SalesLeaveChangeModal,
  },
  data: () => ({
    leaveAnnual: null,
    month: format(new Date(), 'yyyy-MM'),
    model: null,
    participants: [],
    selectedUserId: null,
    selectedDate: '',
    selectedDayDetail: null,
    activeModal: '',
    timezoneName: '',
    loading: false,
    scheduleOpening: false,
    returningToMySchedule: false,
    monthNavigation: 0,
    dayLoading: false,
    daySaving: false,
    saving: false,
    schedulePending: {},
    scheduleDayDetails: {},
    scheduleReasonRequired: false,
    error: '',
    dayError: '',
    dayOffError: '',
    leaveError: '',
    dayOffDraft: [],
    overrideReason: '',
    absence: { type: '', from_date: '', to_date: '', reason: '' },
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    timezoneOptions: [],
  }),
  computed: {
    summary() {
      return this.model ? this.model.summary : {}
    },
    selectedMonthLabel() {
      return format(parseISO(`${this.month}-01`), 'MMMM yyyy')
    },
    monthlyHoursHelp() {
      let hours = this.summary.hours_max
      if (this.summary.hours_min !== this.summary.hours_max) {
        hours = `${this.summary.hours_min}–${this.summary.hours_max}`
      }
      return `Schedule ${hours} hours this month. Monthly progress does not lock a week.`
    },
    weekendMetricLabel() {
      if (this.summary.weekend_requirement_kind === 'shifts') {
        return 'Weekend shifts'
      }
      return 'Weekend work days'
    },
    weekendMetricUnit() {
      if (this.summary.weekend_requirement_kind === 'shifts') return 'shifts'
      return 'days'
    },
    weekendMetricHelp() {
      if (this.summary.weekend_requirement_kind === 'shifts') {
        return `Schedule at least ${this.summary.weekend_work_required} shifts across this month’s Saturday and Sunday dates.`
      }
      return `Work on at least ${this.summary.weekend_work_required} unique Saturday or Sunday dates during the month.`
    },
    legendItems() {
      const templates = this.model ? this.model.templates : []
      return [
        ...templates.map((template) => ({
          key: `template-${template.code}`,
          tone: template.kind,
          label: template.code,
          description: `${template.start_local_time}–${template.end_local_time} company time`,
        })),
        {
          key: 'worked',
          tone: 'worked',
          label: '1–2 shifts',
          description: 'Normal scheduled workload',
        },
        {
          key: 'heavy',
          tone: 'heavy',
          label: '3 shifts',
          description: 'Heavy scheduled day',
        },
        {
          key: 'day-off',
          tone: 'day-off',
          label: 'Day off',
          description: 'Planned day off',
        },
        {
          key: 'leave-approved',
          tone: 'leave-approved',
          label: 'Approved leave',
          description: 'Approved vacation, sick or unpaid leave',
        },
        {
          key: 'leave-requested',
          tone: 'leave-requested',
          label: 'Requested leave',
          description: 'Pending request; not approved yet',
        },
        {
          key: 'today',
          tone: 'today',
          label: 'Today',
          description: 'Current date',
        },
        {
          key: 'locked',
          tone: 'locked',
          label: 'Locked',
          description: 'Read-only planning period',
        },
      ]
    },
    canSelectManager() {
      return Boolean(this.model && this.model.permissions.can_view_others)
    },
    canMutateSchedule() {
      return Boolean(this.model && this.model.permissions.can_mutate_schedule)
    },
    viewingPeerSchedule() {
      if (!this.model || this.canMutateSchedule) return false
      return Number(this.selectedUserId) !== Number(
        this.model.permissions.viewer_user_id
      )
    },
    scheduleReadOnlyReason() {
      if (this.canMutateSchedule || !this.model) return ''
      return 'This schedule is read-only.'
    },
    currentMonthDays() {
      return this.model ? this.model.days.filter((day) => day.is_current_month) : []
    },
    selectedDay() {
      if (!this.model) return null
      return this.model.days.find((day) => day.date === this.selectedDate) || null
    },
    dayShifts() {
      return this.selectedDayDetail ? this.selectedDayDetail.shifts || [] : []
    },
    selectedDayRequiresReason() {
      return this.dayShifts.some(
        (shift) => shift.is_editable && shift.requires_reason
      )
    },
    hasOverrideReason() {
      return Boolean((this.overrideReason || '').trim())
    },
    scheduleSaving() {
      return Object.keys(this.schedulePending).length > 0
    },
    dayOffRequiresReason() {
      return this.currentMonthDays.some(
        (day) => day.is_day_off !== this.dayOffDraft.includes(day.date) &&
          day.requires_reason
      )
    },
    canSaveDayOffs() {
      return Boolean(
        this.dayOffDraft.length === this.summary.planned_days_off_required &&
        !this.saving &&
        (!this.dayOffRequiresReason || this.hasOverrideReason)
      )
    },
    selectedLeaveType() {
      if (!this.model) return null
      return (this.model.leave_types || []).find(
        (item) => item.code === this.absence.type
      ) || null
    },
    plannerLeaveTypes() {
      if (!this.model) return []
      return [...(this.model.leave_types || [])].sort((left, right) => {
        return SALES_LEAVE_TYPE_ORDER.indexOf(left.code) - SALES_LEAVE_TYPE_ORDER.indexOf(right.code)
      })
    },
    leaveReasonRequired() {
      return Boolean(
        this.selectedLeaveType && this.selectedLeaveType.reason_required
      )
    },
    leaveFormValid() {
      return Boolean(
        this.absence.type &&
          this.absence.from_date &&
          this.absence.to_date &&
          !this.leaveLocalError
      )
    },
    leaveLocalError() {
      if (!this.selectedLeaveType || !this.absence.from_date || !this.absence.to_date) return ''
      return validateNewRequest({
        type: this.selectedLeaveType,
        from_date: this.absence.from_date,
        to_date: this.absence.to_date,
        reason: this.absence.reason,
      })
    },
    leaveDays() {
      const { from_date: from, to_date: to } = this.absence
      return from && to && from < to ? inclusiveDays(from, to) : 0
    },
    changeableLeaves() {
      if (!this.model || !this.canMutateSchedule) return []
      return (this.model.absences || [])
        .filter((item) => item.kind === 'leave' && item.to_date >= businessToday())
        .map((item) => ({
          ...item,
          days: inclusiveDays(item.from_date, item.to_date),
        }))
    },
    selectedManagerLabel() {
      return this.model ? this.model.participant.user.name : ''
    },
    selectedTimezoneLabel() {
      const option = this.timezoneOptions.find(
        (item) => item.value === this.timezoneName
      )
      return option ? option.name : this.timezoneName
    },
    todayDate() {
      return this.zonedDate(
        new Date(),
        this.model ? this.model.plan.planning_timezone : 'UTC'
      )
    },
  },
  watch: {
    'absence.type': 'refreshLeaveAnnual',
    'absence.from_date': 'refreshLeaveAnnual',
    'absence.to_date': 'refreshLeaveAnnual',
  },
  created() {
    this.assignmentMutationQueue = Promise.resolve()
    if (/^\d{4}-\d{2}$/.test(this.$route.query.month || '')) {
      this.month = this.$route.query.month
    }
    if (this.$route.query.user_id) {
      this.selectedUserId = Number(this.$route.query.user_id)
    }
    this.loadMonth()
  },
  mounted() {
    const layout = this.$el.closest('.default-layout')
    if (layout) layout.classList.add('shift-planner-layout')
    window.addEventListener('keydown', this.handleEscape)
  },
  beforeDestroy() {
    const layout = this.$el.closest('.default-layout')
    if (layout) layout.classList.remove('shift-planner-layout')
    window.removeEventListener('keydown', this.handleEscape)
  },
  methods: {
    participantLabel(item) {
      return item.user.name
    },
    metricViolation(...codes) {
      const violation = (this.summary.violations || []).find((item) => codes.includes(item.code))
      return violation ? violation.details : ''
    },
    errorText(error) {
      return (
        error?.response?.data?.details ||
        error?.response?.data?.detail ||
        'Request failed. Check your connection and try again.'
      )
    },
    scheduleMutationErrorText(error) {
      const data = error?.response?.data
      const reasonError = data?.reason || data?.errors?.reason || data?.details?.reason
      if (reasonError) return 'Supervisor change reason is required.'
      return this.errorText(error)
    },
    notify(title, type = 'success') {
      this.$notify({ type, title })
    },
    async loadMonth(includeCapacity = false) {
      this.loading = true
      this.error = ''
      try {
        const params = { month: this.month }
        if (this.selectedUserId) params.user_id = this.selectedUserId
        if (includeCapacity === true) params.include_capacity = 1
        const { data } = await getShiftPlannerManagerMonth(params)
        this.model = data
        this.participants = data.participants || []
        this.selectedUserId = data.participant.user.id
        this.timezoneName = data.timezone
        this.timezoneOptions = data.timezone_options || []
        this.ensureSelectedTimezoneOption()
        return true
      } catch (error) {
        this.error = this.errorText(error)
        return false
      } finally {
        this.loading = false
      }
    },
    async showMySchedule() {
      if (!this.model) return
      this.returningToMySchedule = true
      try {
        this.selectedUserId = this.model.permissions.viewer_user_id
        await this.loadMonth()
      } finally {
        this.returningToMySchedule = false
      }
    },
    ensureSelectedTimezoneOption() {
      if (
        this.timezoneName &&
        !this.timezoneOptions.some((item) => item.value === this.timezoneName)
      ) {
        this.timezoneOptions.unshift({
          name: this.timezoneName.replace(/_/g, ' '),
          value: this.timezoneName,
        })
      }
    },
    async changeMonth(amount) {
      if (this.monthNavigation) return
      this.monthNavigation = amount
      try {
        this.month = format(
          addMonths(parseISO(`${this.month}-01`), amount),
          'yyyy-MM'
        )
        await this.loadMonth()
      } finally {
        this.monthNavigation = 0
      }
    },
    focusModalHeader(ref) {
      this.$nextTick(() => {
        const header = this.$refs[ref]
        if (header) header.focusClose()
      })
    },
    openRulesModal() {
      this.activeModal = 'rules'
      this.$modal.show('planner-rules')
      this.focusModalHeader('rulesHeader')
    },
    closeRulesModal() {
      this.$modal.hide('planner-rules')
      this.activeModal = ''
    },
    async openDayModal(day) {
      this.selectedDate = day.date
      this.selectedDayDetail = null
      this.dayError = ''
      this.overrideReason = ''
      this.activeModal = 'day'
      this.$modal.show('planner-day')
      this.focusModalHeader('dayHeader')
      await this.loadSelectedDay()
    },
    closeDayModal() {
      if (this.daySaving) return
      this.$modal.hide('planner-day')
      this.selectedDate = ''
      this.selectedDayDetail = null
      this.dayError = ''
      this.overrideReason = ''
      this.activeModal = ''
    },
    async loadSelectedDay() {
      if (!this.selectedDate) return
      this.dayLoading = true
      this.dayError = ''
      try {
        const { data } = await getShiftPlannerDayDetail({
          date: this.selectedDate,
          user_id: this.selectedUserId,
        })
        this.selectedDayDetail = data
        this.$set(this.scheduleDayDetails, this.selectedDate, data)
      } catch (error) {
        this.dayError = this.errorText(error)
      } finally {
        this.dayLoading = false
      }
    },
    async openScheduleModal() {
      if (!this.canMutateSchedule || this.scheduleOpening) return
      this.scheduleOpening = true
      try {
        const loaded = await this.loadMonth(true)
        if (!loaded || !this.model || !this.canMutateSchedule) return
        this.scheduleDayDetails = {}
        this.scheduleReasonRequired = this.currentMonthDays.some(
          (day) => this.effectiveDayEditable(day) && day.requires_reason
        )
        this.overrideReason = ''
        this.activeModal = 'schedule'
        this.$modal.show('planner-schedule')
        this.focusModalHeader('scheduleHeader')
      } finally {
        this.scheduleOpening = false
      }
    },
    async closeScheduleModal() {
      await this.assignmentMutationQueue
      this.$modal.hide('planner-schedule')
      this.scheduleDayDetails = {}
      this.scheduleReasonRequired = false
      this.overrideReason = ''
      this.activeModal = ''
      await this.loadMonth()
    },
    openDayOffModal() {
      if (!this.canMutateSchedule) return
      this.dayOffDraft = [...this.model.planned_days_off]
      this.dayOffError = ''
      this.overrideReason = ''
      this.activeModal = 'days-off'
      this.$modal.show('planner-days-off')
      this.focusModalHeader('dayOffHeader')
    },
    closeDayOffModal() {
      this.$modal.hide('planner-days-off')
      this.dayOffError = ''
      this.overrideReason = ''
      this.activeModal = ''
    },
    canToggleDayOff(day) {
      return (
        day.is_current_month &&
        this.effectiveDayEditable(day) &&
        !this.absenceFor(day.date)
      )
    },
    dayOffTooltip(day) {
      if (!this.effectiveDayEditable(day)) return this.dayLockedReason(day)
      return this.absenceFor(day.date) ? 'A leave request covers this date.' : ''
    },
    toggleDayOff(day) {
      const index = this.dayOffDraft.indexOf(day.date)
      if (index >= 0) {
        this.dayOffDraft.splice(index, 1)
        this.dayOffError = ''
      } else if (this.dayOffDraft.length < this.summary.planned_days_off_required) {
        this.dayOffDraft.push(day.date)
        this.dayOffError = ''
      } else {
        this.dayOffError = 'Remove one selected day before adding another.'
      }
    },
    async saveDayOffs() {
      this.saving = true
      this.dayOffError = ''
      const previousDates = [...this.model.planned_days_off]
      const previousDays = this.currentMonthDays.map((day) => ({
        date: day.date,
        is_day_off: day.is_day_off,
        assignment_mask: day.assignment_mask,
      }))
      const previousSummary = {
        planned_days_off: this.summary.planned_days_off,
        hours: this.summary.hours,
        weekend_work: this.summary.weekend_work,
        weekend_shifts: this.summary.weekend_shifts,
        weekend_work_days: this.summary.weekend_work_days,
      }

      this.$set(this.model, 'planned_days_off', [...this.dayOffDraft])
      this.currentMonthDays.forEach((day) => {
        const isDayOff = this.dayOffDraft.includes(day.date)
        this.$set(day, 'is_day_off', isDayOff)
        if (isDayOff) this.$set(day, 'assignment_mask', '000000')
      })
      this.recomputeSummary()
      try {
        const { data } = await putShiftPlannerDayOffs({
          participant_id: this.model.participant.id,
          dates: this.dayOffDraft,
          reason: this.overrideReason,
        })
        if (data && data.week_versions) {
          this.$set(this.model, 'week_versions', data.week_versions)
        }
        this.closeDayOffModal()
        this.notify('Day off plan saved')
      } catch (error) {
        this.$set(this.model, 'planned_days_off', previousDates)
        previousDays.forEach((snapshot) => {
          const day = this.model.days.find((item) => item.date === snapshot.date)
          if (!day) return
          this.$set(day, 'is_day_off', snapshot.is_day_off)
          this.$set(day, 'assignment_mask', snapshot.assignment_mask)
        })
        Object.entries(previousSummary).forEach(([key, value]) => {
          this.$set(this.summary, key, value)
        })
        this.dayOffError = this.errorText(error)
      } finally {
        this.saving = false
      }
    },
    openLeaveModal() {
      this.leaveError = ''
      this.activeModal = 'leave'
      this.$modal.show('planner-leave')
      this.focusModalHeader('leaveHeader')
    },
    closeLeaveModal() {
      this.$modal.hide('planner-leave')
      this.leaveError = ''
      this.activeModal = ''
    },
    handleEscape(event) {
      if (event.key !== 'Escape' || !this.activeModal) return
      if (this.activeModal === 'day') this.closeDayModal()
      else if (this.activeModal === 'rules') this.closeRulesModal()
      else if (this.activeModal === 'schedule') this.closeScheduleModal()
      else if (this.activeModal === 'days-off') this.closeDayOffModal()
      else if (this.activeModal === 'leave') this.closeLeaveModal()
    },
    async requestLeave() {
      this.saving = true
      this.leaveError = ''
      try {
        await postShiftPlannerLeaveRequest({
          ...this.absence,
          participant_id: this.model.participant.id,
        })
        this.absence = { type: '', from_date: '', to_date: '', reason: '' }
        this.closeLeaveModal()
        this.notify('Leave request created')
        await this.loadMonth()
      } catch (error) {
        this.leaveError = this.errorText(error)
      } finally {
        this.saving = false
      }
    },
    async saveTimezone() {
      try {
        await patchShiftPlannerSettings({ timezone: this.timezoneName })
        this.notify('Display timezone saved')
      } catch (error) {
        this.notify(this.errorText(error), 'error')
      }
    },
    isLeaveFromDateDisabled(value) {
      // Leave rules use the project date (UTC+3), not the plan timezone.
      const range = newRequestStartRange(
        this.absence.type || 'paid_leave'
      )
      const date = format(value, 'yyyy-MM-dd')
      return date < range.min || Boolean(range.max && date > range.max)
    },
    isLeaveToDateDisabled(value) {
      const date = format(value, 'yyyy-MM-dd')
      return date <= businessToday() || Boolean(
        this.absence.from_date && date <= this.absence.from_date
      )
    },
    leavePreviewRequest(payload) {
      if (this.model.permissions.can_edit_own) return postEmployeesSalesLeaveSelfPreview(payload)
      return postEmployeesSalesLeaveRequestPreview({
        ...payload,
        employee: this.model.participant.employee_id,
      })
    },
    refreshLeaveAnnual: debounce(async function () {
      this.leaveAnnual = null
      const type = this.selectedLeaveType
      const { from_date: from, to_date: to } = this.absence
      if (!type || type.code !== PAID_CODE || !from || !to || this.leaveLocalError) return
      try {
        const payload = { type: type.code, from_date: from, to_date: to }
        this.leaveAnnual = (await this.leavePreviewRequest(payload)).data.annual
      } catch (error) {
        this.leaveAnnual = null
      }
    }, 300),
    openLeaveChange(item) {
      this.$refs.leaveChangeModal.open(item)
    },
    submitLeaveChange(leave, payload) {
      return postShiftPlannerLeaveChangeRequest({
        participant_id: this.model.participant.id,
        leave_id: leave.id,
        ...payload,
      })
    },
    previewLeaveChange(leave, payload) {
      if (this.model.permissions.can_edit_own) {
        return postEmployeesSalesLeaveSelfPreview({ leave: leave.id, ...payload })
      }
      return postEmployeesSalesLeaveRequestPreview({
        employee: this.model.participant.employee_id,
        leave: leave.id,
        ...payload,
      })
    },
    async onLeaveChangeCreated() {
      this.closeLeaveModal()
      await this.loadMonth()
    },
    ownAssignment(shift) {
      return (
        shift.roster.find(
          (item) => (item.user && item.user.id === this.model.participant.user.id) ||
            item.participant_id === this.model.participant.id
        ) || null
      )
    },
    dayShiftBlockedReason(shift) {
      if (!this.selectedDay) return 'No date selected.'
      if (!this.selectedDay.is_current_month) return 'Outside the selected month.'
      if (!this.effectiveDayEditable(this.selectedDay)) {
        return this.dayLockedReason(this.selectedDay)
      }
      if (!shift.is_editable) return shift.locked_reason || 'This shift is locked.'
      if (this.selectedDay.is_day_off) return 'Remove the planned day off first.'
      if (this.approvedAbsenceFor(this.selectedDate)) {
        return 'Approved leave covers this date.'
      }
      if (shift.requires_reason && !this.hasOverrideReason) {
        return 'Add a supervisor change reason first.'
      }
      if (this.ownAssignment(shift)) return ''
      if (this.summary.hours >= this.summary.hours_max) {
        return `The monthly limit of ${this.summary.hours_max} hours has been reached.`
      }
      if (
        !this.model.permissions.can_edit_others &&
        shift.assigned_count >= shift.template.max_people
      ) return 'This shift is full.'
      const limit = this.shiftLimitError(this.selectedDay, shift.template)
      if (limit) return limit
      return ''
    },
    canToggleDayShift(shift) {
      return !this.daySaving && !this.dayShiftBlockedReason(shift)
    },
    async toggleDayShift(shift) {
      if (!this.canToggleDayShift(shift)) return
      const current = this.ownAssignment(shift)
      const rosterBefore = [...shift.roster]
      const maskBefore = this.selectedDay.assignment_mask
      const hoursBefore = this.summary.hours
      const weekendBefore = this.summary.weekend_work
      const weekendShiftsBefore = this.summary.weekend_shifts
      const weekendDaysBefore = this.summary.weekend_work_days
      const optimisticAssignment = {
        id: `optimistic-${shift.id}`,
        participant_id: this.model.participant.id,
        user: this.model.participant.user,
      }
      let roster = [...rosterBefore, optimisticAssignment]
      if (current) {
        roster = rosterBefore.filter((item) => item.id !== current.id)
      }

      this.daySaving = true
      this.dayError = ''
      this.setShiftRoster(shift, roster)
      this.setDayAssignment(this.selectedDate, shift.template.code, !current)
      this.setDayCapacity(this.selectedDate, shift.template.code, roster.length)

      try {
        const payload = {
          reason: this.overrideReason,
        }
        let response = null
        if (current) {
          response = await postShiftPlannerAssignmentCancel(current.id, payload)
        } else {
          response = await postShiftPlannerAssignment({
            ...payload,
            shift_instance_id: shift.id,
            participant_id: this.selectedDayDetail.participant.id,
          })
        }
        if (!current) {
          this.setShiftRoster(
            shift,
            shift.roster.map((item) => item === optimisticAssignment ? response.data : item
            )
          )
        }
        this.$set(
          this.selectedDayDetail,
          'week_version',
          response.data.week_version
        )
        this.$set(
          this.model.week_versions,
          this.selectedDay.week_start,
          response.data.week_version
        )
      } catch (error) {
        this.setShiftRoster(shift, rosterBefore)
        this.$set(this.selectedDay, 'assignment_mask', maskBefore)
        this.setDayCapacity(
          this.selectedDate,
          shift.template.code,
          rosterBefore.length
        )
        this.$set(this.summary, 'hours', hoursBefore)
        this.$set(this.summary, 'weekend_work', weekendBefore)
        this.$set(this.summary, 'weekend_shifts', weekendShiftsBefore)
        this.$set(this.summary, 'weekend_work_days', weekendDaysBefore)
        this.dayError = this.errorText(error)
      } finally {
        this.daySaving = false
      }
    },
    setShiftRoster(shift, roster) {
      this.$set(shift, 'roster', roster)
      this.$set(shift, 'assigned_count', roster.length)
      let state = 'ok'
      if (roster.length < shift.template.min_people) state = 'deficit'
      else if (roster.length >= shift.template.max_people) state = 'full'
      this.$set(shift, 'capacity_state', state)
    },
    setDayAssignment(date, code, selected) {
      const day = this.model.days.find((item) => item.date === date)
      const index = this.model.templates.findIndex(
        (item) => item.code === code
      )
      if (!day || index < 0) return
      const bits = day.assignment_mask.split('')
      bits[index] = selected ? '1' : '0'
      this.$set(day, 'assignment_mask', bits.join(''))
      this.recomputeSummary()
    },
    setDayCapacity(date, code, assigned) {
      const day = this.model.days.find((item) => item.date === date)
      const template = this.model.templates.find((item) => item.code === code)
      if (!day || !template) return
      if (!day.capacity) {
        this.$set(
          day,
          'capacity',
          this.model.templates.map((item) => ({
            shift_id: null,
            code: item.code,
            assigned: 0,
            free: item.max_people,
            min: item.min_people,
            max: item.max_people,
            state: item.min_people ? 'deficit' : 'ok',
          }))
        )
      }
      const cell = day.capacity.find((item) => item.code === code)
      if (!cell) return
      this.$set(cell, 'assigned', assigned)
      this.$set(cell, 'free', Math.max(cell.max - assigned, 0))
      this.$set(
        cell,
        'state',
        assigned < cell.min ? 'deficit' : assigned >= cell.max ? 'full' : 'ok'
      )
    },
    recomputeSummary() {
      const count = this.currentMonthDays.reduce(
        (sum, item) => sum +
          item.assignment_mask.split('').filter((bit) => bit === '1').length,
        0
      )
      const weekendDays = this.currentMonthDays.filter(
        (item) => item.is_weekend && item.assignment_mask.includes('1')
      ).length
      const weekendShifts = this.currentMonthDays.reduce(
        (sum, item) => {
          if (!item.is_weekend) return sum
          return sum + item.assignment_mask
            .split('')
            .filter((bit) => bit === '1').length
        },
        0
      )
      this.$set(this.summary, 'hours', count * 4)
      this.$set(this.summary, 'weekend_shifts', weekendShifts)
      this.$set(
        this.summary,
        'weekend_work_days',
        weekendDays
      )
      let weekendWork = weekendDays
      if (this.summary.weekend_requirement_kind === 'shifts') {
        weekendWork = weekendShifts
      }
      this.$set(this.summary, 'weekend_work', weekendWork)
      this.$set(
        this.summary,
        'planned_days_off',
        this.currentMonthDays.filter((item) => item.is_day_off).length
      )
    },
    shiftLimitError(day, template) {
      const position = this.model.templates.findIndex(
        (item) => item.code === template.code
      )
      if (!day || day.assignment_mask[position] === '1') return ''
      if (
        day.assignment_mask.split('').filter((bit) => bit === '1').length >= 3
      ) return 'You can select a maximum of three shifts per day.'
      const minimumDaysOff = Number(
        this.summary.minimum_days_off_per_week || 0
      )
      if (minimumDaysOff && !day.assignment_mask.includes('1')) {
        const workingDays = this.model.days.filter(
          (item) => item.week_start === day.week_start &&
            item.assignment_mask.includes('1')
        ).length
        if (workingDays >= 7 - minimumDaysOff) {
          return `At least ${minimumDaysOff} full days off are required in every week.`
        }
      }
      let run = 0
      let longest = 0
      this.model.days.forEach((item) => {
        let mask = item.assignment_mask
        if (item.date === day.date) mask = `${mask.slice(0, position)}1${mask.slice(position + 1)}`
        mask.split('').forEach((bit) => {
          run = bit === '1' ? run + 1 : 0
          longest = Math.max(longest, run)
        })
      })
      return longest > 3 ? 'You can select a maximum of three consecutive shifts, including across adjacent days.' : ''
    },
    scheduleShiftKey(day, template) {
      return `${day.date}:${template.code}`
    },
    scheduleShiftPending(day, template) {
      return Boolean(this.schedulePending[this.scheduleShiftKey(day, template)])
    },
    scheduleShiftSelected(day, template) {
      const index = this.model.templates.findIndex(
        (item) => item.code === template.code
      )
      return index >= 0 && day.assignment_mask[index] === '1'
    },
    scheduleCapacity(day, template) {
      const capacity = (day.capacity || []).find(
        (item) => item.code === template.code
      )
      return capacity || {
        shift_id: null,
        code: template.code,
        assigned: 0,
        free: template.max_people,
        min: template.min_people,
        max: template.max_people,
        state: template.min_people ? 'deficit' : 'ok',
      }
    },
    scheduleCellClasses(day, template) {
      const capacity = this.scheduleCapacity(day, template)
      return [
        `sp-schedule-cell--${template.kind}`,
        `is-${capacity.state}`,
        {
          'is-active': this.scheduleShiftSelected(day, template),
          'is-pending': this.scheduleShiftPending(day, template),
          'is-limit-blocked': this.scheduleShiftLimitBlocked(day, template),
          'is-hours-limit-blocked': this.monthlyHoursLimitBlocked(day, template),
        },
      ]
    },
    compactShiftTime(template) {
      const compact = (value) => value.replace(/:00$/, '')
      return `${compact(template.start_local_time)}–${compact(template.end_local_time)}`
    },
    scheduleTemplateTooltip(template) {
      return `${template.code}: ${template.start_local_time}–${template.end_local_time} · company local time (${this.model.plan.planning_timezone})`
    },
    scheduleCellTooltip(day, template) {
      const capacity = this.scheduleCapacity(day, template)
      const selected = this.scheduleShiftSelected(day, template)
      const availability = `${capacity.assigned} assigned · ${capacity.free} free of ${capacity.max}`
      let hoursRestriction = ''
      if (this.monthlyHoursLimitBlocked(day, template)) {
        hoursRestriction = `The monthly limit of ${this.summary.hours_max} hours has been reached.`
      }
      const restriction = hoursRestriction || this.shiftLimitError(day, template) ||
        this.scheduleShiftTooltip(day, template)
      return [
        selected ? 'Selected for you.' : '',
        `${availability}.`,
        restriction,
      ].filter(Boolean).join(' ')
    },
    scheduleCellAriaLabel(day, template) {
      const action = this.scheduleShiftSelected(day, template) ? 'Remove' : 'Select'
      return `${action} ${template.code} on ${this.formatSelectedDate(day.date)}. ${this.scheduleCellTooltip(day, template)}`
    },
    cachedScheduleShift(day, template) {
      const detail = this.scheduleDayDetails[day.date]
      if (!detail) return null
      return (detail.shifts || []).find(
        (shift) => shift.template.code === template.code
      ) || null
    },
    scheduleShiftLimitBlocked(day, template) {
      return Boolean(
        !this.scheduleShiftSelected(day, template) &&
        this.shiftLimitError(day, template)
      )
    },
    monthlyHoursLimitBlocked(day, template) {
      return Boolean(
        !this.scheduleShiftSelected(day, template) &&
        this.summary.hours >= this.summary.hours_max
      )
    },
    canToggleScheduleShift(day, template) {
      const shift = this.cachedScheduleShift(day, template)
      const capacity = this.scheduleCapacity(day, template)
      const selected = this.scheduleShiftSelected(day, template)
      return Boolean(
        day.is_current_month &&
        this.effectiveDayEditable(day) &&
        capacity.is_editable !== false &&
        (!shift || shift.is_editable) &&
        (!capacity.requires_reason || this.hasOverrideReason) &&
        (selected || capacity.free > 0 || this.model.permissions.can_edit_others) &&
        !this.scheduleShiftLimitBlocked(day, template) &&
        !this.monthlyHoursLimitBlocked(day, template) &&
        !day.is_day_off &&
        !this.approvedAbsenceFor(day.date) &&
        !this.scheduleShiftPending(day, template)
      )
    },
    scheduleShiftTooltip(day, template) {
      const shift = this.cachedScheduleShift(day, template)
      const capacity = this.scheduleCapacity(day, template)
      if (!day.is_current_month) return 'Outside the selected month.'
      if (!this.effectiveDayEditable(day)) return this.dayLockedReason(day)
      if (capacity.is_editable === false) {
        return capacity.locked_reason || 'This shift is locked.'
      }
      if (shift && !shift.is_editable) {
        return shift.locked_reason || 'This shift is locked.'
      }
      if (day.is_day_off) return 'Remove the planned day off first.'
      if (this.approvedAbsenceFor(day.date)) return 'Approved leave covers this date.'
      if (capacity.requires_reason && !this.hasOverrideReason) {
        return 'Add a supervisor change reason first.'
      }
      return `${template.code} · ${template.start_local_time}–${template.end_local_time} company time`
    },
    scheduleDayTooltip(day) {
      if (!day.is_current_month) return 'Outside the selected month.'
      if (!this.effectiveDayEditable(day)) return this.dayLockedReason(day)
      if (day.is_day_off) return 'Remove the planned day off first.'
      if (this.approvedAbsenceFor(day.date)) return 'Approved leave covers this date.'
      return ''
    },
    scheduleDayClasses(day) {
      return {
        'is-outside': !day.is_current_month,
        'is-past': day.date < this.todayDate,
        'is-today': day.date === this.todayDate,
        'is-weekend': day.is_weekend,
        'is-day-off': day.is_day_off,
        'is-absence-approved': this.approvedAbsenceFor(day.date),
        'is-absence-requested': this.requestedAbsenceFor(day.date),
        'is-locked': !this.effectiveDayEditable(day),
      }
    },
    async scheduleDayDetail(day) {
      const cached = this.scheduleDayDetails[day.date]
      const weekVersion = this.model.week_versions[day.week_start]
      if (cached && cached.week_version === weekVersion) return cached
      const { data } = await getShiftPlannerDayDetail({
        date: day.date,
        user_id: this.selectedUserId,
      })
      if (
        (data.shifts || []).some(
          (shift) => shift.is_editable && shift.requires_reason
        )
      ) {
        this.scheduleReasonRequired = true
      }
      this.$set(this.scheduleDayDetails, day.date, data)
      return data
    },
    async toggleScheduleShift(day, template) {
      if (!this.canToggleScheduleShift(day, template)) return
      const key = this.scheduleShiftKey(day, template)
      this.$set(this.schedulePending, key, true)

      const mutation = async () => {
        let shift = null
        let rosterBefore = null
        let maskBefore = ''
        let hoursBefore = 0
        let weekendBefore = 0
        try {
          const detail = await this.scheduleDayDetail(day)
          shift = (detail.shifts || []).find(
            (item) => item.template.code === template.code
          )
          if (!shift) {
            this.notify(
              'This shift is not available for the selected day.',
              'error'
            )
            return
          }
          if (!shift.is_editable) {
            this.notify(
              shift.locked_reason || 'This shift is locked.',
              'error'
            )
            return
          }
          const current = this.ownAssignment(shift)
          if (!current && this.summary.hours >= this.summary.hours_max) return
          if (
            !current &&
            !this.model.permissions.can_edit_others &&
            shift.assigned_count >= shift.template.max_people
          ) {
            this.notify('This shift is full.', 'error')
            return
          }
          const limit = current ? '' : this.shiftLimitError(day, template)
          if (limit) {
            this.notify(limit, 'error')
            return
          }
          if (shift.requires_reason && !this.hasOverrideReason) {
            this.notify('Supervisor change reason is required.', 'error')
            return
          }
          const payload = {
            reason: this.overrideReason,
          }
          rosterBefore = [...shift.roster]
          maskBefore = day.assignment_mask
          hoursBefore = this.summary.hours
          weekendBefore = {
            weekend_work: this.summary.weekend_work,
            weekend_shifts: this.summary.weekend_shifts,
            weekend_work_days: this.summary.weekend_work_days,
          }
          const optimisticAssignment = {
            id: `optimistic-${key}`,
            participant_id: detail.participant.id,
            user: detail.participant.user,
          }
          let roster = [...rosterBefore, optimisticAssignment]
          if (current) {
            roster = rosterBefore.filter((item) => item.id !== current.id)
          }
          this.setShiftRoster(shift, roster)
          this.setDayAssignment(day.date, template.code, !current)
          this.setDayCapacity(day.date, template.code, roster.length)

          let response = null
          if (current) {
            response = await postShiftPlannerAssignmentCancel(
              current.id,
              payload
            )
          } else {
            response = await postShiftPlannerAssignment({
              ...payload,
              shift_instance_id: shift.id,
              participant_id: detail.participant.id,
            })
            this.setShiftRoster(
              shift,
              shift.roster.map((item) => item === optimisticAssignment ? response.data : item
              )
            )
          }
          this.$set(detail, 'week_version', response.data.week_version)
          this.$set(
            this.model.week_versions,
            day.week_start,
            response.data.week_version
          )
          this.model.days
            .filter((item) => item.week_start === day.week_start)
            .forEach((item) => this.$set(item, 'week_version', response.data.week_version)
            )
        } catch (error) {
          if (shift && rosterBefore) {
            this.setShiftRoster(shift, rosterBefore)
            this.$set(day, 'assignment_mask', maskBefore)
            this.setDayCapacity(
              day.date,
              template.code,
              rosterBefore.length
            )
            this.$set(this.summary, 'hours', hoursBefore)
            Object.entries(weekendBefore).forEach(([field, value]) => {
              this.$set(this.summary, field, value)
            })
          }
          const message = this.scheduleMutationErrorText(error)
          this.notify(message, 'error')
        } finally {
          this.$delete(this.schedulePending, key)
        }
      }
      this.assignmentMutationQueue = this.assignmentMutationQueue.then(
        mutation,
        mutation
      )
      await this.assignmentMutationQueue
    },
    absenceFor(date) {
      return (this.model.absences || []).find(
        (item) => item.from_date <= date && item.to_date >= date
      )
    },
    approvedAbsenceFor(date) {
      const absence = this.absenceFor(date)
      return absence && absence.status === 'approved' ? absence : null
    },
    requestedAbsenceFor(date) {
      const absence = this.absenceFor(date)
      return absence && absence.status === 'requested' ? absence : null
    },
    absenceLabel(date) {
      const absence = this.absenceFor(date)
      return absence && absence.status === 'requested' ? 'Requested' : 'Leave'
    },
    absenceTitle(date) {
      const absence = this.absenceFor(date)
      if (!absence) return ''
      const status = absence.status === 'requested' ? 'Requested' : 'Approved'
      return `${status}: ${absence.type.name}`
    },
    effectiveDayEditable(day) {
      return Boolean(this.canMutateSchedule && day.is_editable)
    },
    dayLockedReason(day) {
      if (!this.canMutateSchedule) return this.scheduleReadOnlyReason
      return day.locked_reason || ''
    },
    selectedCodes(day) {
      return this.model.templates
        .filter((item, index) => day.assignment_mask[index] === '1')
        .map((item) => item.code)
    },
    dayClasses(day) {
      const shiftCount = this.selectedCodes(day).length
      const canHighlightWork =
        !day.is_day_off && !this.approvedAbsenceFor(day.date)

      return {
        'sp-day--outside': !day.is_current_month,
        'sp-day--worked': canHighlightWork && shiftCount > 0 && shiftCount < 3,
        'sp-day--heavy': canHighlightWork && shiftCount >= 3,
        'sp-day--past': day.date < this.todayDate,
        'sp-day--today': this.todayDate === day.date,
        'sp-day--off': day.is_day_off,
        'sp-day--absence-approved': this.approvedAbsenceFor(day.date),
        'sp-day--absence-requested': this.requestedAbsenceFor(day.date),
        'sp-day--locked': !this.effectiveDayEditable(day),
      }
    },
    formatDayNumber(value) {
      return format(parseISO(value), 'd')
    },
    formatDayMonth(value) {
      return format(parseISO(value), 'MMM').toUpperCase()
    },
    formatSelectedDate(value) {
      return format(parseISO(value), 'EEEE, d MMMM yyyy')
    },
    formatScheduleDate(value) {
      return format(parseISO(value), 'EEE, d MMM')
    },
    dayAriaLabel(day) {
      return `${this.formatSelectedDate(day.date)}, ${
        this.selectedCodes(day).join(', ') || 'no shifts'
      }${this.dayLockedReason(day) ? `, ${this.dayLockedReason(day)}` : ''}`
    },
    localShiftRange(shift) {
      if (!this.selectedDate) {
        return 'Exact local time is unavailable'
      }
      let startInstant = this.shiftTemplateInstant(shift.template, false)
      let endInstant = this.shiftTemplateInstant(shift.template, true)
      if (shift.starts_at) startInstant = new Date(shift.starts_at)
      if (shift.ends_at) endInstant = new Date(shift.ends_at)
      const start = this.zonedParts(startInstant, this.timezoneName)
      const end = this.zonedParts(endInstant, this.timezoneName)
      const startDate = `${start.year}-${start.month}-${start.day}`
      const endDate = `${end.year}-${end.month}-${end.day}`
      const startOffset = differenceInCalendarDays(
        parseISO(startDate),
        parseISO(this.selectedDate)
      )
      const endOffset = differenceInCalendarDays(
        parseISO(endDate),
        parseISO(this.selectedDate)
      )
      const notes = []
      if (startOffset) notes.push(`starts ${this.offsetLabel(startOffset)}`)
      if (endOffset && endOffset !== startOffset) {
        notes.push(`ends ${this.offsetLabel(endOffset)}`)
      }
      const range = `${start.hour}:${start.minute}–${end.hour}:${end.minute}`
      return notes.length ? `${range} · ${notes.join(', ')}` : `${range} · same date`
    },
    shiftTemplateInstant(template, isEnd) {
      const startTime = template.start_local_time
      const endTime = template.end_local_time
      let date = parseISO(this.selectedDate)
      if (isEnd && endTime <= startTime) date = addDays(date, 1)
      return this.zonedLocalDateToInstant(
        format(date, 'yyyy-MM-dd'),
        isEnd ? endTime : startTime,
        this.model.plan.planning_timezone
      )
    },
    zonedLocalDateToInstant(date, time, zone) {
      const desired = new Date(`${date}T${time}:00Z`)
      const actual = this.zonedParts(desired, zone)
      const represented = Date.UTC(
        Number(actual.year),
        Number(actual.month) - 1,
        Number(actual.day),
        Number(actual.hour),
        Number(actual.minute)
      )
      const instant = new Date(desired.getTime() + desired.getTime() - represented)
      const verified = this.zonedParts(instant, zone)
      const verifiedTime = Date.UTC(
        Number(verified.year),
        Number(verified.month) - 1,
        Number(verified.day),
        Number(verified.hour),
        Number(verified.minute)
      )
      return new Date(instant.getTime() + desired.getTime() - verifiedTime)
    },
    offsetLabel(offset) {
      if (offset === -1) return 'previous day'
      if (offset === 1) return 'next day'
      return `${offset > 0 ? '+' : ''}${offset} days`
    },
    rosterLabel(shift) {
      const names = shift.roster
        .map((item) => item.user && item.user.name)
        .filter(Boolean)
      return names.length ? names.join(', ') : 'No one assigned yet'
    },
    capacityTone(shift) {
      if (shift.assigned_count >= shift.template.max_people) return 'red'
      if (shift.assigned_count < shift.template.min_people) return 'amber'
      return 'green'
    },
    capacityLabel(shift) {
      const free = Math.max(
        shift.template.max_people - shift.assigned_count,
        0
      )
      return `${shift.assigned_count}/${shift.template.max_people} · ${
        free ? `${free} free` : 'full'
      }`
    },
    zonedParts(value, zone) {
      return Object.fromEntries(
        new Intl.DateTimeFormat('en-GB', {
          timeZone: zone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        })
          .formatToParts(new Date(value))
          .map((item) => [item.type, item.value])
      )
    },
    zonedDate(value, zone) {
      const p = this.zonedParts(value, zone)
      return `${p.year}-${p.month}-${p.day}`
    },
  },
}
</script>

<style lang="scss" src="./styles.scss"></style>
<style lang="scss" scoped>
.sp-grid--manager-metrics {
  grid-template-columns: repeat(4, minmax(150px, 1fr));
}
.sp-timezone-preview {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $ui-space-12;
  margin-bottom: $ui-space-16;
  padding: $ui-space-16;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background: $ui-surface-soft;
}
.sp-timezone-preview div {
  display: grid;
  min-width: 0;
  gap: $ui-space-4;
}
.sp-timezone-preview span {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}
.sp-timezone-preview strong {
  overflow-wrap: anywhere;
  color: $ui-ink;
  font-size: $ui-font-size-body;
  font-weight: 600;
}
.sp-day-shifts {
  display: grid;
  gap: $ui-space-12;
}
.sp-day-shift {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: $ui-space-16;
  padding: $ui-space-16;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background: $ui-surface;
}
.sp-day-shift.is-selected {
  border-color: $ui-blue;
  background: $ui-blue-soft;
}
.sp-day-shift__copy,
.sp-day-shift__actions {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: $ui-space-8;
}
.sp-day-shift__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-8;
}
.sp-day-shift__title strong {
  color: $ui-ink;
  font-size: $ui-font-size-section-title;
  font-weight: 600;
}
.sp-day-shift__title .sp-day-shift__company-time {
  color: $ui-muted;
  font-size: $ui-font-size-body;
}
.sp-day-shift__copy > span {
  color: $ui-muted;
  font-size: $ui-font-size-body;
}
.sp-day-shift__copy .sp-day-shift__local {
  color: $ui-blue;
  font-weight: 600;
}
.sp-day-shift__copy small {
  overflow-wrap: anywhere;
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}
.sp-day-shift__actions {
  align-items: flex-end;
  justify-content: space-between;
}
.sp-manage-shifts {
  min-width: 142px;
}
.sp-calendar-toolbar {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;
  padding-bottom: 18px;
  border-bottom: 1px solid $ui-line;
}
.sp-calendar-guide {
  display: grid;
  align-items: center;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 16px;
  margin: 18px 0 14px;
}
.sp-calendar-month {
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
}
.sp-calendar-guide p {
  justify-self: end;
  margin: 0;
  color: $ui-muted;
  font-size: 14px;
  text-align: right;
}
.sp-calendar-navigation {
  display: grid;
  min-width: 0;
  align-items: center;
  grid-template-columns: 56px minmax(0, 1fr) 56px;
  gap: 12px;
}
.sp-calendar-nav-button {
  display: inline-flex;
  width: 56px;
  height: 96px;
  align-items: center;
  justify-content: center;
  border: 1px solid $ui-line-strong;
  border-radius: $ui-panel-radius;
  background: $ui-surface-soft;
  color: $ui-blue;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}
.sp-calendar-nav-button:hover,
.sp-calendar-nav-button:focus-visible {
  border-color: $ui-blue;
  background: $ui-blue-soft;
  outline: 0;
}
.sp-calendar-nav-button svg {
  width: 28px;
  height: 28px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}
.sp-calendar {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}
.sp-calendar-scroll {
  min-width: 0;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
}
.sp-weekdays {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 8px;
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}
.sp-weekdays span:nth-child(6),
.sp-weekdays span:nth-child(7) {
  color: $ui-red;
}
.sp-day {
  position: relative;
  display: grid;
  min-width: 0;
  height: 96px;
  min-height: 96px;
  align-items: center;
  padding: 10px 9px;
  border: 1px solid $ui-line-strong;
  border-radius: 12px;
  background: $ui-surface;
  color: $ui-ink;
  grid-template-columns: minmax(0, 1fr);
  text-align: center;
}
.sp-day--worked {
  border-color: #d7e3f3;
  background: #f6f9fd;
}
.sp-day--heavy {
  border-color: #ecc7c3;
  background: #fff3f2;
}
.sp-day--heavy .sp-day__date > strong {
  color: #a84743;
}
.sp-day--off {
  background: $ui-green-soft;
}
.sp-day--absence-approved {
  background: $ui-amber-soft;
}
.sp-day--absence-requested {
  border-color: #9f90dd;
  box-shadow: inset 0 0 0 1px rgba(108, 91, 199, 0.12);
}
.sp-day--past {
  border-color: #e5e8ed;
  background: #f6f7f9;
}
.sp-day--past .sp-day__date > strong {
  color: #929ba7;
}
.sp-day--past .sp-day__date > small,
.sp-day--past .sp-day__date > em {
  color: #a4abb4;
}
.sp-day--past .sp-day__shift i,
.sp-day--past .sp-day__lock {
  opacity: 0.48;
}
.sp-day--today {
  border-color: #53ad88;
  background: #f4fbf7;
  box-shadow: inset 0 0 0 1px #53ad88;
}
.sp-day--today .sp-day__date > strong {
  color: $ui-green;
}
.sp-day__date {
  display: flex;
  min-width: 40px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.sp-day__date > strong {
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: 28px;
  font-weight: 600;
  line-height: 30px;
}
.sp-day__date > small {
  color: $ui-muted;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 14px;
}
.sp-day__date > i {
  width: 5px;
  height: 5px;
  margin-top: 4px;
  border-radius: 50%;
  background: $ui-green;
}
.sp-day__date > em {
  max-width: 76px;
  margin-top: 4px;
  overflow: hidden;
  color: $ui-green;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sp-day--absence-approved .sp-day__date > em {
  color: $ui-amber;
}
.sp-day--absence-requested .sp-day__date > em {
  color: #6653bd;
}
.sp-day__shift-stack {
  position: absolute;
  top: 50%;
  left: 9px;
  display: grid;
  min-width: 0;
  align-content: center;
  gap: 4px;
  justify-items: start;
  transform: translateY(-50%);
}
.sp-day__shift {
  display: block;
  width: 28px;
  height: 6px;
}
.sp-day__shift i {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 4px;
}
.sp-segment--day,
.sp-segment--night {
  background: #e5e9ef;
}
.sp-segment--day.is-active,
.sp-day__shift.is-active .sp-segment--day {
  background: #4c8ce8;
}
.sp-segment--night.is-active,
.sp-day__shift.is-active .sp-segment--night {
  background: #6c5bc7;
}
.sp-day--outside {
  opacity: 0.58;
  background: $ui-surface-soft;
}
.sp-day--locked {
  cursor: default;
  opacity: 0.36;
}
.sp-day--locked:hover {
  transform: none;
}
.sp-day__lock {
  position: absolute;
  right: 50%;
  bottom: 5px;
  width: 10px;
  height: 8px;
  border: 1.5px solid $ui-muted;
  border-radius: 3px;
  transform: translateX(50%);
}
.sp-day__lock::before {
  position: absolute;
  top: -6px;
  left: 1.5px;
  width: 5px;
  height: 6px;
  border: 1.5px solid $ui-muted;
  border-bottom: 0;
  border-radius: 5px 5px 0 0;
  content: "";
}
.sp-schedule-modal .sp-modal__body {
  min-height: 0;
  overflow-y: auto;
}
.sp-modal__field--separated {
  margin-bottom: 12px;
}
.sp-supervisor-reason {
  padding: 14px;
  border: 1px solid $ui-line-strong;
  border-radius: 10px;
  background: $ui-surface-soft;
  transition: border-color .15s ease, background-color .15s ease;
}
.sp-supervisor-reason > small {
  color: $ui-muted;
  font-size: 13px;
  line-height: 18px;
}
.sp-supervisor-reason--required {
  border-color: #efb2ba;
  background: $ui-red-soft;
}
.sp-supervisor-reason--required .sp-field-label,
.sp-supervisor-reason--required > small {
  color: $ui-red;
}
.sp-supervisor-reason__input {
  min-height: 48px;
}
.sp-modal__field--day-off-reason {
  margin-top: 12px;
}
.sp-schedule-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
  gap: 10px;
}
.sp-schedule-summary > div,
.sp-schedule-summary > p {
  margin: 0;
}
.sp-schedule-summary > div {
  display: grid;
  min-width: 230px;
  padding: 10px 12px;
  border: 1px solid $ui-line;
  border-radius: 10px;
  background: $ui-surface-soft;
  grid-template-columns: 1fr auto;
  gap: 2px 10px;
}
.sp-schedule-summary span,
.sp-schedule-summary small {
  color: $ui-muted;
  font-size: 14px;
  line-height: 18px;
}
.sp-schedule-summary strong {
  color: $ui-ink;
  font-size: 24px;
  grid-row: 1 / span 2;
  grid-column: 2;
}
.sp-schedule-summary small {
  grid-column: 1;
}
.sp-schedule-summary > p {
  display: flex;
  min-width: 260px;
  flex: 1 1 320px;
  align-items: center;
  padding: 8px 4px;
  color: $ui-muted;
  font-size: 14px;
  line-height: 20px;
}
.sp-schedule-error,
.sp-schedule-reason {
  margin-bottom: 14px;
}
.sp-schedule-reason {
  display: block;
}
.sp-schedule-board {
  overflow-x: auto;
  padding-bottom: 4px;
  border: 1px solid $ui-line;
  border-radius: 12px;
  overscroll-behavior-inline: contain;
}
.sp-schedule-table {
  width: 100%;
  min-width: 860px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}
.sp-schedule-table th,
.sp-schedule-table td {
  height: 52px;
  padding: 4px;
  border-right: 1px solid $ui-line;
  border-bottom: 1px solid $ui-line;
  background: $ui-surface;
  vertical-align: middle;
}
.sp-schedule-table tr:last-child th,
.sp-schedule-table tr:last-child td {
  border-bottom: 0;
}
.sp-schedule-table th:last-child,
.sp-schedule-table td:last-child {
  border-right: 0;
}
.sp-schedule-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  height: 60px;
  background: $ui-surface-soft;
  color: $ui-ink;
  font-family: $ui-font-heading;
  text-align: center;
}
.sp-schedule-table thead th > strong,
.sp-schedule-table thead th > span {
  display: block;
}
.sp-schedule-table thead th > strong {
  font-size: 17px;
  font-weight: 600;
  line-height: 22px;
}
.sp-schedule-table thead th > span {
  margin-top: 2px;
  color: $ui-muted;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
}
.sp-schedule-table__day-heading {
  width: 152px;
  text-align: left !important;
}
.sp-schedule-table tbody th {
  position: sticky;
  left: 0;
  z-index: 2;
  padding-right: 10px;
  padding-left: 10px;
  background: $ui-surface;
  text-align: left;
}
.sp-schedule-table thead .sp-schedule-table__day-heading {
  left: 0;
  z-index: 4;
}
.sp-schedule-table__date {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.sp-schedule-table__date strong {
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}
.sp-schedule-table__date span {
  overflow: hidden;
  color: $ui-muted;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sp-schedule-table tr.is-weekend th,
.sp-schedule-table tr.is-weekend td {
  background: #fffaf0;
}
.sp-schedule-table tr.is-today th {
  background: $ui-green-soft;
  box-shadow: inset 3px 0 0 $ui-green;
}
.sp-schedule-table tr.is-today .sp-schedule-table__date strong {
  color: $ui-green;
}
.sp-schedule-table tr.is-day-off th {
  background: $ui-green-soft;
}
.sp-schedule-table tr.is-absence-approved th {
  background: $ui-amber-soft;
}
.sp-schedule-table tr.is-absence-requested th {
  background: #f5f2ff;
}
.sp-schedule-table tr.is-past .sp-schedule-table__date strong,
.sp-schedule-table tr.is-locked .sp-schedule-table__date strong {
  color: #8993a1;
}
.sp-schedule-cell {
  position: relative;
  display: flex;
  width: 100%;
  max-width: 92px;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0 auto;
  padding: 4px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: $ui-ink;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease;
}
.sp-schedule-cell:hover:not(:disabled) {
  border-color: #8eb8ef;
  background: $ui-blue-soft;
}
.sp-schedule-cell:focus-visible {
  outline: 2px solid #8eb8ef;
  outline-offset: 1px;
}
.sp-schedule-cell.is-deficit:not(.is-active) {
  box-shadow: inset 3px 0 0 #d6a346;
}
.sp-schedule-cell.is-full:not(.is-active) {
  background: #f0f2f5;
  color: #687383;
}
.sp-schedule-cell.is-limit-blocked:not(.is-active),
.sp-schedule-cell.is-hours-limit-blocked:not(.is-active) {
  border-color: #e1e5eb;
  background: #eef1f5;
  box-shadow: none;
  color: #7d8795;
}
.sp-schedule-cell--day.is-active {
  border-color: #4c8ce8;
  background: #edf4ff;
  box-shadow: inset 3px 0 0 #4c8ce8;
}
.sp-schedule-cell--night.is-active {
  border-color: #6c5bc7;
  background: #f3f0ff;
  box-shadow: inset 3px 0 0 #6c5bc7;
}
.sp-schedule-cell:disabled {
  cursor: not-allowed;
  opacity: 0.48;
}
.sp-schedule-cell.is-pending {
  animation: sp-pulse 1s ease-in-out infinite alternate;
}
.sp-schedule-cell__capacity {
  display: grid;
  min-width: 0;
  text-align: center;
}
.sp-schedule-cell__capacity strong {
  color: inherit;
  font-family: $ui-font-heading;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
}
.sp-schedule-cell__capacity small {
  color: $ui-muted;
  font-size: 12px;
  line-height: 16px;
}
.sp-schedule-cell__selected {
  position: absolute;
  top: 5px;
  right: 5px;
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: $ui-blue;
  color: #fff;
}
.sp-schedule-cell__selected svg {
  width: 12px;
  height: 12px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2.2;
}
.sp-schedule-cell--night .sp-schedule-cell__selected {
  background: #5b49b4;
}
.sp-schedule-save-state {
  color: $ui-muted;
  font-size: 14px;
}
.sp-mini-weekdays,
.sp-mini-calendar {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
}
.sp-day-off-notice {
  display: flex;
  min-height: 64px;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid #b9d4f7;
  border-radius: 10px;
  background: $ui-blue-soft;
  color: #24517f;
  font-size: 14px;
  line-height: 20px;
}
.sp-day-off-notice__icon {
  display: inline-flex;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: $ui-blue;
  color: #fff;
  font-family: $ui-font-heading;
  font-size: 14px;
  font-weight: 600;
}
.sp-day-off-notice--error {
  border-color: #f1a8b1;
  background: $ui-red-soft;
  color: #8f2634;
}
.sp-day-off-notice--error .sp-day-off-notice__icon {
  background: $ui-red;
}
.sp-mini-weekdays {
  margin-bottom: 8px;
  color: #697386;
  font-size: 14px;
  text-align: center;
}
.sp-mini-day {
  min-height: 62px;
  padding: 9px 7px;
  border: 1px solid #d8dee8;
  border-radius: 10px;
  background: #fff;
  color: #17243a;
  font: 600 14px "CoreSansCR";
  cursor: pointer;
}
.sp-mini-day small {
  display: block;
  margin-top: 5px;
  color: #147a55;
  font-size: 14px;
}
.sp-mini-day.is-selected {
  border-color: #63b795;
  background: #eaf8f2;
  box-shadow: 0 0 0 2px rgba(20, 122, 85, 0.1);
}
.sp-mini-day.is-weekend:not(.is-selected) {
  background: #fff8e7;
}
.sp-mini-day.is-outside {
  background: #f7f9fc;
  opacity: 0.32;
}
.sp-mini-day.is-locked {
  cursor: not-allowed;
  opacity: 0.45;
}
@media (max-width: 1100px) {
  .sp-grid--manager-metrics {
    grid-template-columns: repeat(2, minmax(150px, 1fr));
  }
  .sp-day {
    padding-right: 6px;
    padding-left: 6px;
  }
  .sp-day__date {
    min-width: 32px;
  }
  .sp-day__date > strong {
    font-size: 24px;
  }
  .sp-day__shift {
    width: 20px;
  }
  .sp-day__shift-stack {
    left: 6px;
  }
}
@media (max-width: 760px) {
  .sp-timezone-preview,
  .sp-day-shift {
    grid-template-columns: 1fr;
  }
  .sp-day-shift__actions {
    align-items: stretch;
  }
  .sp-calendar-toolbar {
    align-items: stretch;
    flex-direction: column;
  }
  .sp-calendar-guide {
    align-items: start;
    grid-template-columns: 1fr;
  }
  .sp-calendar-guide p {
    justify-self: start;
    text-align: left;
  }
  .sp-calendar-month {
    grid-row: 1;
    justify-self: start;
    text-align: left;
  }
  .sp-calendar-navigation {
    grid-template-columns: 40px minmax(0, 1fr) 40px;
    gap: 6px;
  }
  .sp-calendar-nav-button {
    width: 40px;
    height: 72px;
  }
  .sp-weekdays,
  .sp-calendar {
    min-width: 700px;
  }
  .sp-day {
    padding-right: 12px;
    padding-left: 12px;
  }
  .sp-day__date {
    min-width: 40px;
  }
  .sp-day__date > strong {
    font-size: 28px;
  }
  .sp-day__shift {
    width: 32px;
  }
  .sp-day__shift-stack {
    left: 12px;
  }
  .sp-schedule-summary {
    align-items: stretch;
  }
  .sp-schedule-summary > div {
    min-width: calc(50% - 5px);
  }
}
@media (max-width: 560px) {
  .sp-grid--manager-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
  .sp-schedule-summary > div,
  .sp-schedule-summary > p {
    min-width: 100%;
  }
  .sp-mini-weekdays {
    display: none;
  }
  .sp-mini-calendar {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
